'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import InquiryPanel from './components/InquiryPanel';

// ── Lazy Short-Form Video Tile ────────────────────────────────────────────────
function ShortFormTile({ src, label, delay }: { src: string; label: string; delay: number }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);

  // Only assign src once the tile scrolls into view
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoaded(true);
          obs.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleMouseEnter = () => {
    if (videoRef.current && loaded) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="reveal-item relative w-full overflow-hidden bg-forge-black border border-pewter/10 group cursor-pointer hover:border-brayer-red/40 transition-all duration-200 ease-in-out hover:scale-[1.03] hover:brightness-110 hover:shadow-[0_8px_32px_rgba(0,0,0,0.55)]"
      style={{ aspectRatio: '9/16' }}
      data-delay={String(delay)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {loaded ? (
        <video
          ref={videoRef}
          src={src}
          muted
          loop
          playsInline
          preload="none"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        // Lightweight placeholder while off-screen
        <div className="absolute inset-0 bg-forge-black/80 animate-pulse" />
      )}

      {/* Play icon overlay — visible at rest, hidden on hover */}
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-forge-black/40 group-hover:opacity-0 transition-opacity duration-300">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <polygon points="5,3 19,12 5,21" fill="#C8C2B8" stroke="#C8C2B8" strokeWidth="1" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}

// ── Lazy YouTube Embed (click-to-load) ───────────────────────────────────────
function YouTubeTile({ videoId, title, delay = 0 }: { videoId: string; title: string; delay?: number }) {
  const [active, setActive] = useState(false);
  const thumbnail = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <div
      className="reveal-item aspect-video w-full overflow-hidden relative cursor-pointer group transition-all duration-200 ease-in-out hover:scale-[1.03] hover:brightness-110 hover:shadow-[0_8px_32px_rgba(0,0,0,0.55)]"
      style={{ '--reveal-delay': `${delay}ms` } as React.CSSProperties}
      data-delay={String(delay)}
      onClick={() => setActive(true)}
    >
      {active ? (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full border-0"
        />
      ) : (
        <>
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={thumbnail}
            alt={title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-forge-black/40 group-hover:bg-forge-black/20 transition-colors duration-300">
            <div className="w-12 h-12 rounded-full bg-brayer-red/90 flex items-center justify-center shadow-lg">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <polygon points="6,4 20,12 6,20" fill="white" />
              </svg>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function HomePage() {
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const observerRef = useRef(null);

  const initObserver = useCallback(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const delay = el.dataset.delay ?? '0';
            setTimeout(() => {
              el.classList.add('revealed');
            }, parseInt(delay, 10));
            observerRef.current?.unobserve(el);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -4% 0px' }
    );

    document.querySelectorAll('.reveal-item, .reveal-fade').forEach((el) => {
      observerRef.current?.observe(el);
    });
  }, []);

  useEffect(() => {
    const t = setTimeout(initObserver, 80);
    return () => {
      clearTimeout(t);
      observerRef.current?.disconnect();
    };
  }, [initObserver]);

  const openInquiry = (_printTitle?: string) => {
    setInquiryOpen(true);
  };

  return (
    <main className="relative overflow-x-hidden bg-cotton-rag">
      {/* Global grid lines */}
      <div className="grid-lines">
        <div className="grid-line" />
        <div className="grid-line hidden md:block" />
        <div className="grid-line hidden md:block" />
        <div className="grid-line" />
      </div>

      <Header onInquire={() => openInquiry()} />

      {/* ── LANDING ──────────────────────────────────── */}
      <section id="landing" className="relative min-h-screen flex flex-col items-center justify-center bg-cotton-rag overflow-hidden">
        <div className="noise-overlay" />
        <div className="absolute top-0 left-0 right-0 h-px bg-forge-black/8" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-forge-black/8" />

        <div className="relative z-10 flex flex-col items-center text-center px-4 select-none">
          <h1 className="hero-press hero-press-animate">
            TONY TAYLOR
          </h1>
          <div className="mt-6 md:mt-8 flex items-center gap-4">
            <div className="h-px w-12 bg-pewter/60" />
            <p
              className="text-sm md:text-base tracking-[0.22em] uppercase font-medium text-[#9B2335]"
              style={{ fontFamily: 'Manrope, sans-serif' }}>
              Stop Making Boring Content
            </p>
            <div className="h-px w-12 bg-pewter/60" />
          </div>
          <div className="mt-10 reveal-item" data-delay="300">
            <a
              href="#portfolio"
              className="inline-flex items-center gap-3 px-8 py-4 bg-forge-black text-cotton-rag text-xs font-bold uppercase tracking-[0.18em] hover:bg-brayer-red transition-colors duration-300"
              style={{ fontFamily: 'Manrope, sans-serif' }}>
              View Portfolio
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M8 3v10M3 9l5 5 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-30">
          <span
            className="text-[10px] uppercase tracking-[0.28em] text-pewter"
            style={{ fontFamily: 'Manrope, sans-serif' }}>
            Scroll
          </span>
          <div className="w-px h-10 bg-gradient-to-b from-pewter to-transparent" />
        </div>
      </section>

      {/* ── PORTFOLIO ────────────────────────────────── */}
      <section id="portfolio" className="bg-forge-black py-20 md:py-32 px-4 md:px-8 lg:px-12">

        <div className="max-w-screen-2xl mx-auto relative z-10">
          {/* Section header */}
          <div className="flex items-end justify-between mb-16 reveal-item" data-delay="0">
            <div>
              <span
                className="text-[10px] uppercase tracking-[0.3em] text-pewter/50 block mb-2"
                style={{ fontFamily: 'Manrope, sans-serif' }}>
                Recent work
              </span>
              <h2
                className="text-3xl md:text-5xl font-bold text-plate-gray leading-tight tracking-tight"
                style={{ fontFamily: 'Manrope, sans-serif' }}>
                Portfolio
              </h2>
            </div>
          </div>

          {/* ── MUSIC VIDEOS ── */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-6 reveal-item" data-delay="0">
              <h3
                className="text-xl md:text-2xl font-bold text-plate-gray uppercase tracking-[0.12em]"
                style={{ fontFamily: 'Manrope, sans-serif' }}>
                Music Videos
              </h3>
              <div className="h-px flex-1 bg-brayer-red/40" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
              {[
                { id: 'c9zPQ0qkwuU', title: 'Music Video 1' },
                { id: 'hyoHAeNz0vw', title: 'Music Video 2' },
                { id: 'tjC2qPql0c8', title: 'Music Video 3' },
                { id: '5VQe3c8dbmA', title: 'Music Video 4' },
                { id: 'eqMyvk2EeZs', title: 'Music Video 5' },
                { id: '0oiwOfodWL4', title: 'Music Video 6' },
                { id: 'YH5J3rf2R4A', title: 'Music Video 7' },
                { id: '1DHwPVTvNiE', title: 'Music Video 8' },
                { id: 'K5EiNCunsVk', title: 'Music Video 9' },
                { id: 'JrWosWo4vUs', title: 'Music Video 10' },
              ].map((video, i) => (
                <YouTubeTile key={video.id} videoId={video.id} title={video.title} delay={i * 60} />
              ))}
            </div>
          </div>

          {/* ── PHOTOGRAPHY ── */}
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-6 reveal-item" data-delay="0">
              <h3
                className="text-xl md:text-2xl font-bold text-plate-gray uppercase tracking-[0.12em]"
                style={{ fontFamily: 'Manrope, sans-serif' }}>
                Photography
              </h3>
              <div className="h-px flex-1 bg-brayer-red/40" />
            </div>
            <div className="columns-2 md:columns-3 lg:columns-4 gap-2 md:gap-3 space-y-2 md:space-y-3">
              {[
                { src: 'https://www.dropbox.com/scl/fi/vpycq5lrelezj4rhmrjxx/Vanity-Park-107.jpg?rlkey=t8ss9ik6p0n5lx7bqx7kozmi7&st=vb6x676c&raw=1', alt: 'Vanity Park – atmospheric urban night scene' },
                { src: 'https://www.dropbox.com/scl/fi/abea993yorwmedcgmuk6x/PN-13-1.jpg?rlkey=5ypr0dl4liln07yla5x98ji55&st=7tv5ucd8&raw=1', alt: 'PN-13 – editorial portrait with dramatic lighting' },
                { src: 'https://www.dropbox.com/scl/fi/msjwio9n0o5r3zpjq6979/PN-2.jpg?rlkey=ba1fj5uck7q7690frnzudm279&st=s5xnoj5k&raw=1', alt: 'PN-2 – moody portrait with dark background' },
                { src: 'https://www.dropbox.com/scl/fi/3ulfco6blq8i78n89axwi/BJJ-20-1.jpg?rlkey=x55vtzcx2o99kpiutjr1kkuca&st=zx52202j&raw=1', alt: 'BJJ-20 – action shot with motion and intensity' },
                { src: 'https://www.dropbox.com/scl/fi/k6xms9fmk9fa4n62kg6og/AMPM-197.jpg?rlkey=l1wnm7fiocka3jkspyqktf1u3&st=o2gblh4k&raw=1', alt: 'AMPM – late night convenience store exterior glow' },
                { src: 'https://www.dropbox.com/scl/fi/owk1dhvhnigy8vavtybk9/NTHRD-23.jpg?rlkey=soz7qbfjmoni5zqhrgy6bllsw&st=h5rqn6u7&raw=1', alt: 'NTHRD-23 – live music performance energy' },
                { src: 'https://www.dropbox.com/scl/fi/e17qo9b2fve5nto2svhza/PORT-3.jpg?rlkey=k7equqt7c5u6ibw7c3v0f6xzu&st=twwpdhcr&raw=1', alt: 'PORT-3 – wide landscape portrait with environmental context' },
                { src: 'https://www.dropbox.com/scl/fi/ryysiu9d0he4zmlxvk8kc/HW-7-1.jpg?rlkey=e48yt152vskbd764tjwemtrek&st=ymracf17&raw=1', alt: 'HW-7 – high contrast black and white portrait' },
                { src: 'https://www.dropbox.com/scl/fi/fjm8bnpwhr4bjtse4hk64/FIFTY-1.jpg?rlkey=zxo5o4w8113pzjdqnded6qxd0&st=6k88id10&raw=1', alt: 'Fifty – bold graphic street photography' },
                { src: 'https://www.dropbox.com/scl/fi/3uwh1zlruajxz1bzl470v/PLRS-49.jpg?rlkey=sjjvc16b8ynbbntp9in4da1ks&st=gn2nudxh&raw=1', alt: 'PLRS-49 – band or artist in creative environment' },
                { src: 'https://www.dropbox.com/scl/fi/g6t5dfeyag65wchbv1zur/LAB-121.png?rlkey=rm8qiyjdepna0rx41i7f7jbwh&st=upsitqtn&raw=1', alt: 'LAB-121 – studio session behind the scenes' },
                { src: 'https://www.dropbox.com/scl/fi/e6uh2maxuqq4dkm9kpxf8/LAB-18.png?rlkey=vmv80xu95f510olm5vf6z2kiu&st=hl6vte3f&raw=1', alt: 'LAB-18 – creative studio lighting experiment' },
                { src: 'https://www.dropbox.com/scl/fi/oxa5payp7ylb53hxa2o3o/MB-5.jpg?rlkey=f1qv2gb5kzrgva8pyz9czsy9i&st=d8psnvk0&raw=1', alt: 'MB-5 – intimate portrait with soft directional light' },
              ].map((photo, i) => (
                <div
                  key={i}
                  className="reveal-item break-inside-avoid overflow-hidden group cursor-pointer mb-2 md:mb-3"
                  data-delay={String(i * 55)}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto block transition-all duration-200 ease-in-out group-hover:scale-[1.03] group-hover:brightness-110 group-hover:shadow-[0_8px_32px_rgba(0,0,0,0.55)]"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* ── SHORT FORM ── */}
          <div>
            <div className="flex items-center gap-4 mb-6 reveal-item" data-delay="0">
              <h3
                className="text-xl md:text-2xl font-bold text-plate-gray uppercase tracking-[0.12em]"
                style={{ fontFamily: 'Manrope, sans-serif' }}>
                Short Form
              </h3>
              <div className="h-px flex-1 bg-brayer-red/40" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 md:gap-3">
              {[
                { src: 'https://www.dropbox.com/scl/fi/yg1fhtbntiu79u1e2uguc/Water-Pour-2-CAPTIONS.mp4?rlkey=aqr0zleurr69ywxmkq4ato9zn&st=o9vdx9s8&raw=1', label: 'Water Pour' },
                { src: 'https://www.dropbox.com/scl/fi/6h8rhdkrctwldpwvu2vee/Vanity-Park-Reel-2.mp4?rlkey=zmrzz5uyqv7u2u484hs535jct&st=xd6p4834&raw=1', label: 'Vanity Park' },
                { src: 'https://www.dropbox.com/scl/fi/169c2mdp1my9jp9rsbph2/MIRROR-REEL.mp4?rlkey=yiu5k041cjlkeabcqzjy2q0ks&st=2h5fitth&raw=1', label: 'Mirror Reel' },
                { src: 'https://www.dropbox.com/scl/fi/6i8keoktg1n8s9oemsgnb/LTLI-BRISBANE-RECAP.mp4?rlkey=uj2ix0i2mxgh4276uzof31lre&st=cr6cofol&raw=1', label: 'LTLI Brisbane' },
                { src: 'https://www.dropbox.com/scl/fi/1mpyzx2gzvd4rfonipgf3/LORNA-SHORE-SHORT-INTERVIEW.mp4?rlkey=uk83f5vzl8da107kusb4bfohe&st=gs1lv4xv&raw=1', label: 'Lorna Shore' },
                { src: 'https://www.dropbox.com/scl/fi/4k2ipca8usdpsa06vuid9/LIVE-REEL-4K.mp4?rlkey=dv1514lwgn6ofrc3j2icw62pz&st=pqgs8zxx&raw=1', label: 'Live Reel 4K' },
                { src: 'https://www.dropbox.com/scl/fi/4ql3kpgwpos5b4cyjg2v1/FISHEYE-BREAKDOWN-MASK.mp4?rlkey=42nnxf6ph8xbiw27urni8nwhx&st=9k282dph&raw=1', label: 'Fisheye Breakdown' },
                { src: 'https://www.dropbox.com/scl/fi/4qm6dcuhpbb1thd9b1quo/ESPRESSO-TONIC-MAIN-REEL.mp4?rlkey=sepyhwnp8g1osoim2kzsvtnbs&st=u4sibxjv&raw=1', label: 'Espresso Tonic' },
              ].map((video, i) => (
                <ShortFormTile key={i} src={video.src} label={video.label} delay={i * 70} />
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ── ABOUT ────────────────────────────────────── */}
      <section id="about" className="bg-cotton-rag py-24 md:py-36 px-4 md:px-12 relative">
        <div className="noise-overlay" style={{ opacity: 0.025 }} />
        <div className="absolute top-0 left-0 right-0 h-px bg-forge-black/8" />

        <div className="max-w-3xl mx-auto relative z-10">
          <div className="reveal-item" data-delay="0">
            <span
              className="text-[10px] uppercase tracking-[0.3em] text-pewter/60 block mb-6"
              style={{ fontFamily: 'Manrope, sans-serif' }}>
              About
            </span>
            <h2
              className="text-4xl md:text-6xl font-bold text-forge-black leading-tight tracking-tight mb-8"
              style={{ fontFamily: 'Manrope, sans-serif' }}>
              Tony Taylor
            </h2>
          </div>

          <p
            className="text-pewter text-lg md:text-xl font-light leading-relaxed reveal-item mb-6"
            data-delay="100"
            style={{ fontFamily: 'Manrope, sans-serif' }}>
            Music &amp; Alternative Culture; I create photo and video for bands, brands, and events -shot with intention.
          </p>
          <p
            className="text-pewter text-lg md:text-xl font-light leading-relaxed reveal-item mb-10"
            data-delay="180"
            style={{ fontFamily: 'Manrope, sans-serif' }}>
            No stock aesthetics. No corporate polish. Just honest visuals that feel real — raw, alive, and worth keeping.
          </p>

          <div className="flex items-center gap-6 reveal-item" data-delay="260">
            <span
              className="text-xs uppercase tracking-[0.22em] text-pewter/60"
              style={{ fontFamily: 'Manrope, sans-serif' }}>
              Melbourne, AUS
            </span>
            <div className="h-px w-8 bg-brayer-red/60" />
            <button
              onClick={() => openInquiry()}
              className="inline-flex items-center gap-3 px-7 py-3.5 bg-forge-black text-cotton-rag text-xs font-bold uppercase tracking-[0.18em] hover:bg-brayer-red transition-colors duration-300"
              style={{ fontFamily: 'Manrope, sans-serif' }}>
              Enquire
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Inquiry Panel */}
      <InquiryPanel
        isOpen={inquiryOpen}
        onClose={() => { setInquiryOpen(false); }}
      />
    </main>
  );
}
